import { IcAlert, IcCompass, IcSpeed } from '@assets/icons/24x24';
import { IcEmissionDetection, IcProcess } from '@assets/icons/32x32';
import { Center, For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import DataSheetCardLink from '@components/common/layout/DataSheetCardLink';
import { useMapContext } from '@components/map/MapContext';
import TargetElevationsInformation from '@components/map/popup/infos/targets/information/TargetElevationsInformation';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useWithDispatch } from '@hooks/useWithDispatch';
import {
  hasAnyActionRoleOnSensors,
  hasSensorRoleFromActiveProfile,
  selectActiveOrganization,
} from '@redux/authent/authent.selectors';
import { updateSelectedNotificationId } from '@redux/global/global.reducer';
import { selectSelectedNotificationId } from '@redux/global/global.selector';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectJammingNeutralSensorStatus } from '@redux/situation/monitoring.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { RootState } from '@redux/store';
import { booleanPointInPolygon } from '@turf/turf';
import { formatDate, formatDurationFromTo } from '@utils/date.utils';
import { extendedSensorShortHand } from '@utils/map/map.constants';
import { getSector } from '@utils/map/sector.utils';
import { getSelectedSectorConfiguration } from '@utils/sensors/neutral/neutral.utils';
import { isDrone, isSpatial } from '@utils/target.utils';
import { AngleUnits, convertSpeedUnitFromMPerS, formatUnit, SpeedUnits } from '@utils/unit.utils';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

import { Target } from '@/types/c2/c2.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { ProcedureStateEnum } from '@/types/config/procedure.types';
import { DroneNotificationLight, NotificationTypeEnum } from '@/types/data/data.types';
import { DateTimeEnum } from '@/types/dateTime.types';
import { PopupTypeEnum } from '@/types/popup.types';
import { SensorFamilyEnum, TargetLocation } from '@/types/sensor/sensor.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

import CoordinatesInformation from '../../../common/CoordinatesInformation';
import PopupContainer from '../../../common/PopupContainer';
import { SEE_NOTIFICATIONS_ROLE } from '../TargetInfo';
import DroneInformation from './DroneInformation';

type Props = {
  target: Target<TargetLocation>;
  notification?: DroneNotificationLight;
  position: { x: number; y: number };
};

export default function TargetInformation({ target, notification, position }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const selectedNotification = useAppSelector(selectSelectedNotificationId);
  const jammingSensorStatus = useSelectorWithReplayMode(selectJammingNeutralSensorStatus);
  const neutralSensorAction = { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.NEUTRAL };
  const organization = useAppSelector(selectActiveOrganization);
  const accessNeutral = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, neutralSensorAction));
  const dispatchUpdateSelectedNotification = useWithDispatch(updateSelectedNotificationId);
  const canSeeNotification = useAppSelector((state: RootState) =>
    hasAnyActionRoleOnSensors(state, SEE_NOTIFICATIONS_ROLE),
  );
  const { mapId } = useMapContext();
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const situationTime = useSelectorWithReplayMode(selectSituationTime);

  const formattedStartDate = formatDate(target.detectionStart, DateTimeEnum.DATE);
  const formattedEndDate = formatDate(target.detectionEnd, DateTimeEnum.DATE);
  const formattedStartTime = format(new Date(target.detectionStart), "HH':'mm':'ss");
  const formattedEndTime = format(new Date(target.detectionEnd), "HH':'mm':'ss");

  const isSameDay = formattedStartDate === formattedEndDate;

  const sensors = target.trackIds.reduce<Partial<Record<SensorTypeEnum, string[]>>>(
    (acc, { sensorType, sensorCode }) => {
      if (!acc[sensorType]) {
        acc[sensorType] = [];
      }
      acc[sensorType].push(sensorCode);
      return acc;
    },
    {} as Partial<Record<SensorTypeEnum, string[]>>,
  );

  const isDroneTarget = isDrone(target, organization);

  function isInsideJammingSector() {
    if (accessNeutral && jammingSensorStatus && target.lastPosition.location?.position) {
      const jammingSector = getSelectedSectorConfiguration(jammingSensorStatus);
      if (jammingSector === null) {
        return false;
      }
      const bearing1 = jammingSector.orientation - jammingSector.aperture / 2;
      const bearing2 = jammingSector.orientation + jammingSector.aperture / 2;

      const sectorPolygon = getSector(
        jammingSensorStatus.configuration.sensorPosition,
        bearing1,
        bearing2,
        jammingSector.range,
      );
      return (
        sectorPolygon !== null &&
        booleanPointInPolygon(toLonLatArray(target.lastPosition.location.position, false)!, sectorPolygon)
      );
    }
    return false;
  }

  function handleHistoryPopup() {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.HISTORY,
        data: {
          identifiers: [target.id],
          type: NotificationTypeEnum.DRONE,
          displayId: target.displayId,
          openingDate: situationTime ? new Date(situationTime) : new Date(),
        },
        position,
      },
    });
  }

  return (
    <VStack gap={0.25} width="100%" height="100%">
      {isInsideJammingSector() ? (
        <Center
          width="100%"
          height="160px"
          backgroundColor="red.900"
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          backgroundPosition="center"
          userSelect="none"
          backgroundImage="url(/jamming-animated-background.gif)"
        >
          <Text
            textAlign="center"
            whiteSpace="pre-line"
            textStyle="screenStd"
            backgroundColor="neutral.900-op75"
            paddingY="standardS"
            paddingX="standardM"
          >
            <FormattedMessage id="targetInfos.tab.information.neutral" />
          </Text>
        </Center>
      ) : null}
      {isDroneTarget && <DroneInformation target={target} notification={notification} />}
      <PopupContainer>
        <VStack gap="standardXS" width="100%">
          {isSpatial(target) && (
            <TargetElevationsInformation
              maxTransmittedAgl={target.maxTransmittedAgl}
              maxComputedAgl={target.maxComputedAgl}
              maxTransmittedAsl={target.maxTransmittedAsl}
              maxComputedAsl={target.maxComputedAsl}
              location={target.lastPosition.location!}
            />
          )}
          <HStack width="100%" gap={3}>
            <HStack gap={1} flex={1}>
              <Icon as={IcSpeed} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <Text textStyle="screenStd" fontWeight="medium">
                {formatUnit(
                  SpeedUnits.KILOMETERS_PER_HOUR,
                  convertSpeedUnitFromMPerS(SpeedUnits.KILOMETERS_PER_HOUR, target.lastPosition.location?.speed),
                )}
              </Text>
            </HStack>
            <HStack gap={1} flex={1}>
              <Icon as={IcSpeed} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                <FormattedMessage id="global.max" />
              </Text>
              <Text textStyle="screenStd" fontWeight="medium">
                {formatUnit(
                  SpeedUnits.KILOMETERS_PER_HOUR,
                  convertSpeedUnitFromMPerS(SpeedUnits.KILOMETERS_PER_HOUR, target.maxSpeed),
                )}
              </Text>
            </HStack>
          </HStack>
          <HStack width="100%" gap={3}>
            <HStack gap={1} flex={1}>
              <Icon as={IcCompass} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <Text textStyle="screenStd" fontWeight="medium">
                {formatUnit(AngleUnits.TURN, target.lastPosition.location?.heading)}
              </Text>
            </HStack>
            <HStack gap={1} flex={1}>
              <Icon as={IcEmissionDetection} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <For each={Object.entries(sensors)}>
                {([key, values]) => (
                  <Tooltip
                    key={key}
                    content={
                      <VStack gap={0.25}>
                        {values.map((code) => (
                          <Text
                            paddingX={1.5}
                            paddingY={1}
                            key={code}
                            textStyle="screenStd"
                            backgroundColor="neutral.black"
                          >
                            {code}
                          </Text>
                        ))}
                      </VStack>
                    }
                  >
                    <Text
                      paddingX={0.5}
                      paddingY={0.25}
                      key={key}
                      userSelect="none"
                      textStyle="screenStd"
                      color="neutral.300"
                      backgroundColor="neutral.black"
                    >
                      {extendedSensorShortHand[key as SensorTypeEnum]}
                    </Text>
                  </Tooltip>
                )}
              </For>
            </HStack>
          </HStack>
        </VStack>
      </PopupContainer>
      <HStack gap={0.25} width="100%" cursor="default" align="stretch">
        <Center height="auto" flex={1} backgroundColor="neutral.700" paddingY="standardS" paddingX="standardM">
          <Text textStyle="screenStd">{formatDurationFromTo(target.detectionStart, target.detectionEnd)}</Text>
        </Center>
        <Center
          height="100%"
          paddingY="standardS"
          paddingX="standardM"
          flexShrink={0}
          width={{ base: '238px', '4k': '264px' }}
          backgroundColor="neutral.700"
        >
          <Text color="neutral.300" textStyle="screenStd" whiteSpace="pre-line">
            <FormattedMessage
              id={isSameDay ? 'targetInfos.tab.information.sameDay' : 'targetInfos.tab.information.multipleDays'}
              values={{
                startDate: (
                  <Text as="span" key="formattedStartDate" textStyle="screenStd" color="neutral.white">
                    {formattedStartDate}
                  </Text>
                ),
                startTime: (
                  <Text as="span" key="formattedStartTime" textStyle="screenStd" color="neutral.white">
                    {formattedStartTime}
                  </Text>
                ),
                endDate: (
                  <Text as="span" key="formattedEndDate" textStyle="screenStd" color="neutral.white">
                    {formattedEndDate}
                  </Text>
                ),
                endTime: (
                  <Text as="span" key="formattedEndTime" textStyle="screenStd" color="neutral.white">
                    {formattedEndTime}
                  </Text>
                ),
              }}
            />
          </Text>
        </Center>
      </HStack>
      {target.lastPosition.location?.position && (
        <CoordinatesInformation coordinates={target.lastPosition.location.position} />
      )}
      {notification && notification.procedureState !== ProcedureStateEnum.DONE && (
        <DataSheetCardLink
          label={formatMessage({ id: 'targetInfos.tab.information.process' })}
          optionalLabel={formatMessage(
            { id: 'targetInfos.tab.information.state' },
            { state: notification.procedureState },
          )}
          optionalLabelColor={
            notification.procedureState === ProcedureStateEnum.TO_PROCESS ? 'neutral.300' : 'green.500'
          }
          icon={IcProcess}
          isDisabled={selectedNotification === notification.id}
          onClick={() => dispatchUpdateSelectedNotification(notification.id)}
        />
      )}
      {canSeeNotification && isDroneTarget && target.displayId !== null && (
        <DataSheetCardLink
          label={formatMessage({ id: 'targetInfos.tab.information.history' })}
          icon={IcAlert}
          onClick={handleHistoryPopup}
        />
      )}
    </VStack>
  );
}
